<template>
  <v-expansion-panel-content>
    <template v-slot:header>
      <h6 class="title">
        <v-icon
          class="mr-2"
        >
          percent
        </v-icon>
        Interest Terms
      </h6>
    </template>
    <v-card>
      <v-container
        fluid
        grid-list-sm
      >
        <v-layout
          row
          wrap
        >
          <v-flex
            xs4
            sm2
          >
            <v-select
              v-model="interestTerms.profit_calculation_formula"
              :items="profitCalculationFormulas"
              item-text="name"
              :color="colorTheme"
              item-value="value"
              label="Profit Calculation Formula"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :readonly="isFieldReadonly"
            />
          </v-flex>
          <v-flex
            xs4
            sm2
          >
            <v-select
              v-model="interestTerms.interval"
              :items="intervals"
              item-text="name"
              :color="colorTheme"
              item-value="value"
              label="Interval"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :readonly="isFieldReadonly"
            />
          </v-flex>
          <v-flex
            xs4
            sm2
          >
            <custom-field
              :type="'date'"
              :color-theme="colorTheme"
              :value="interestTerms.runtime_start"
              label="Runtime Start"
              :on-change="e => onDatePickerChange(e, 'runtime_start')"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :disabled="isFieldReadonly"
            />
          </v-flex>
          <v-flex
            xs4
            sm2
          >
            <custom-field
              :type="'date'"
              :color-theme="colorTheme"
              :value="interestTerms.runtime_end_max"
              label="Runtime End Max"
              :on-change="e => onDatePickerChange(e, 'runtime_end_max')"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :disabled="isFieldReadonly"
            />
          </v-flex>
          <v-flex
            xs4
            sm2
          >
            <custom-field
              :type="'date'"
              :color-theme="colorTheme"
              :value="interestTerms.runtime_end_min"
              label="Runtime End Min"
              :on-change="e => onDatePickerChange(e, 'runtime_end_min')"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :disabled="isFieldReadonly"
            />
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex
            xs4
            sm2
          >
            <v-text-field
              v-model="interestTerms.rates.rate_at_maturity"
              label="Rate At Maturity"
              :color="colorTheme"
              type="number"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :readonly="isFieldReadonly"
            />
          </v-flex>
          <v-flex
            xs4
            sm2
          >
            <v-text-field
              v-model="interestTerms.rates.delay_interest_rate"
              label="Delay Interest Rate"
              :color="colorTheme"
              type="number"
              :class="isFieldReadonly ? 'is-readonly' : ''"
              :readonly="isFieldReadonly"
            />
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex
            v-if="interestTerms.recurring_payout_dates"
          >
            <strong>Recurring Payout Dates:</strong>
            <p>Recurring Payout Dates NOT include the Final Maturity Payout!</p>
            <div
              v-for="(dates, year) in payoutDates"
              :key="year"
            >
              <div
                v-for="(payout_date, index) in dates"
                :key="index"
                class="d-inline-block"
              >
                <v-chip
                  :color="yearColor(year, 'darken-3')"
                  text-color="white"
                  style="float:left;"
                >
                  <v-avatar
                    :class="yearColor(year, 'lighten-1')"
                  >
                    {{ payout_date[2] + payout_date[3] }}
                  </v-avatar>
                  {{ payout_date }}
                </v-chip>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
  </v-expansion-panel-content>
</template>

<script>

import CustomField from '@/components/financialProducts/shared/CustomField';
import IntervalsEnum from '@/enum/financialProducts/Intervals';
import ProfitCalculationFormulasEnum from '@/enum/financialProducts/ProfitCalculationFormulas';

export default {
  name: 'InterestTermsExpansionPanel',
  components: {
    CustomField,
  },
  props: {
    interestTerms: {
      type: Object,
      default: () => {},
    },
    stage: {
      type: String,
      required: true,
    },
    colorTheme: {
      type: String,
      default: 'blue',
    },
    type: {
      type: String,
      required: true,
    },
  },
  computed: {
    profitCalculationFormulas() {
      return Object.keys(ProfitCalculationFormulasEnum).map(key => ({
        name: ProfitCalculationFormulasEnum[key],
        value: ProfitCalculationFormulasEnum[key],
      }));
    },
    intervals() {
      return Object.keys(IntervalsEnum).map(key => ({
        name: IntervalsEnum[key],
        value: IntervalsEnum[key],
      }));
    },
    isFieldReadonly() {
      return this.stage !== 'draft' || this.type === 'tranche';
    },
    payoutDates() {
      const original = this.interestTerms.recurring_payout_dates;
      const sorted = original.sort();

      return sorted.reduce((dates, dateString) => {
        const year = dateString.slice(0, 4);
        if (!dates[year]) {
          dates[year] = [];
        }
        dates[year].push(dateString);

        return dates;
      }, {});
    },
  },
  methods: {
    onDatePickerChange(date, field) {
      if (!this.isFieldReadonly) {
        this.interestTerms[field] = date.value;
      }
    },
    yearColor(year, colorVariant = 'darken-4') {
      const num = this.yearNumber(year);
      const color = [
        'red',
        'pink',
        'purple',
        'deep-purple',
        'indigo',
        'blue',
        'light-blue',
        'cyan',
        'teal',
        'green',
        'light-green',
        'lime',
        'yellow',
        'amber',
        'orange',
        'deep-orange',
        'brown',
        'blue-grey',
      ];

      return `${color[num]} ${colorVariant}`;
    },
    yearNumber(year) {
      const years = Object.keys(this.payoutDates);

      return year - years[0];
    },
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/financialProducts/InterestTermsExpansionPanel.vue